/**
 * DeviceSource describes a source location on a device, from which transfers can start.
 * (e.g. a well or reservoir).
 */
export class DeviceSource {
  constructor(name: string, maxVolume: number, discreteSteps: [number], label: string) {
    this.name = name;
    this.maxVolume = maxVolume;
    this.discreteSteps = discreteSteps;
    this.label = label;
  }
  name: string;
  maxVolume: number;
  discreteSteps: number[];
  label: string;
}

/**
 * DeviceConfig specifies a specific configuration of a device, e.g. all available sources
 * and properties of those sources.
 */
export class DeviceConfig {
  constructor(id: string, description: string, sources: DeviceSource[]) {
    this.id = id;
    this.description = description;
    this.sources = sources;
  }
  id: string;
  description: string;
  sources: DeviceSource[];
}

/** Utility function to generate standard A1 format well names. **/
function allWells(rows: number, cols: number): string[] {
  return [...Array(rows).keys()].map(i => String.fromCharCode(i + 'A'.charCodeAt(0))).map(
    rowLetter => [...Array(cols).keys()].map(c => rowLetter + (c + 1))
  ).flat()
}

// Note all volumes are in ul.
const echoSources: DeviceSource[] = allWells(8, 12).map(well => new DeviceSource(well, 65, [2.5e-3], ""))

const dragonfly6Sources: DeviceSource[] = [
  // Note all volumes are in ul.
  new DeviceSource("Channel1", 10e3, [200e-3], ""),
  new DeviceSource("Channel2", 10e3, [200e-3], ""),
  new DeviceSource("Channel3", 10e3, [200e-3], ""),
  new DeviceSource("Channel4", 10e3, [200e-3], ""),
  new DeviceSource("Channel5", 1.5e3, [130e-3], "LV"),
  new DeviceSource("Channel6", 1.5e3, [130e-3], "LV")
]

/**
 * Hard coded device configurations for use during development. These should be removed and
 * replaced when a more general device management system has been created.
 */
const fixedDeviceConfigs: DeviceConfig[] = [
  new DeviceConfig("1", "Manual", []),
  new DeviceConfig("2", "Echo 650", echoSources),
  new DeviceConfig("3", "Dragonfly 6 Channel", dragonfly6Sources),
]

export function manualDeviceConfig(): DeviceConfig {
  return lookupDeviceConfig("1")
}

export function allDeviceConfigs(): DeviceConfig[] {
  return fixedDeviceConfigs;
}

export function lookupDeviceConfig(id : string): DeviceConfig {
  const foundDevice = fixedDeviceConfigs.find(d => d.id === id);
  // If we can't find the device, default to manual.
  return foundDevice ?? fixedDeviceConfigs[0];
}